import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolsAPI } from '../../utils/api';

// State code → full name map (for display)
const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',
  NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',
  ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',
  RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',
  UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',
  WI:'Wisconsin',WY:'Wyoming',DC:'Washington D.C.',
};

// Haversine distance in miles between two lat/lng points
const haversineMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8;
  const toRad = x => x * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Geocode a school city+state (or zip) → {lat,lng} via Nominatim; caches in sessionStorage
const geocodeSchool = async (city, state, zip) => {
  if (!city && !zip) return null;
  const cacheKey = city
    ? `sk2g_geo:${city.toLowerCase()}:${(state || '').toLowerCase()}`
    : `sk2g_geo:zip:${zip}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch {}
  // 1️⃣ Try city + state lookup first
  if (city) {
    try {
      const stateName = STATE_NAMES[state?.toUpperCase()] || state || '';
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&city=${encodeURIComponent(city)}&state=${encodeURIComponent(stateName)}&country=US`;
      const r = await fetch(url);
      const data = await r.json();
      if (data?.[0]?.lat) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        try { sessionStorage.setItem(cacheKey, JSON.stringify(coords)); } catch {}
        return coords;
      }
    } catch {}
  }
  // 2️⃣ Fall back to zip lookup (handles typos in city name or missing city)
  if (zip) {
    try {
      const zipKey = `sk2g_geo:zip:${zip}`;
      const cached = sessionStorage.getItem(zipKey);
      if (cached) {
        const coords = JSON.parse(cached);
        try { sessionStorage.setItem(cacheKey, JSON.stringify(coords)); } catch {}
        return coords;
      }
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&postalcode=${encodeURIComponent(zip)}&country=US`;
      const r = await fetch(url);
      const data = await r.json();
      if (data?.[0]?.lat) {
        const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        try { sessionStorage.setItem(zipKey, JSON.stringify(coords)); } catch {}
        try { sessionStorage.setItem(cacheKey, JSON.stringify(coords)); } catch {}
        return coords;
      }
    } catch {}
  }
  return null;
};

const SchoolCard = ({ school, onSelect, highlight, distance }) => {
  const grades = school.grades_supported ? school.grades_supported.split(',') : [];
  return (
    <div
      onClick={() => onSelect(school)}
      style={{
        background: '#fff',
        borderRadius: 16,
        border: highlight ? '2px solid #6366f1' : '1.5px solid #e5e7eb',
        padding: '1.4rem',
        cursor: 'pointer',
        transition: 'transform 0.18s, box-shadow 0.18s',
        boxShadow: highlight ? '0 4px 20px rgba(99,102,241,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 28px rgba(99,102,241,0.2)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = highlight ? '0 4px 20px rgba(99,102,241,0.15)' : '0 2px 8px rgba(0,0,0,0.06)';
      }}
    >
      {highlight && (
        <span style={{
          position: 'absolute', top: 12, right: 12,
          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
          color: '#fff', fontSize: '0.65rem', fontWeight: 700,
          padding: '2px 8px', borderRadius: 20, letterSpacing: '0.05em', textTransform: 'uppercase',
        }}>Near You</span>
      )}
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏫</div>
      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#111827', lineHeight: 1.3, marginBottom: '0.35rem' }}>
        {school.name}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <span>📍 {school.city}{school.state ? `, ${school.state}` : ''}</span>
        {distance != null && (
          <span style={{ background: '#f0fdf4', color: '#15803d', fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>
            {distance} mi
          </span>
        )}
      </div>
      {grades.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
          {grades.slice(0, 6).map(g => (
            <span key={g} style={{
              background: '#ede9fe', color: '#7c3aed',
              fontSize: '0.68rem', fontWeight: 700,
              padding: '2px 8px', borderRadius: 20,
            }}>Gr {g.trim()}</span>
          ))}
          {grades.length > 6 && (
            <span style={{ fontSize: '0.68rem', color: '#9ca3af', alignSelf: 'center' }}>+{grades.length - 6} more</span>
          )}
        </div>
      )}
      <div style={{
        marginTop: '1rem',
        background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
        color: '#fff', borderRadius: 10, padding: '0.45rem',
        textAlign: 'center', fontSize: '0.8rem', fontWeight: 700,
      }}>
        View Kits →
      </div>
    </div>
  );
};

const SchoolKitsLandingPage = () => {
  const [allSchools, setAllSchools] = useState([]);
  const [nearbySchools, setNearbySchools] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [location, setLocation] = useState(null);
  const [locating, setLocating] = useState(true);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef();

  // Fetch all schools
  useEffect(() => {
    schoolsAPI.getAll()
      .then(r => setAllSchools(r.data || []))
      .catch(() => setAllSchools([]))
      .finally(() => setLoadingSchools(false));
  }, []);

  // IP-based geolocation — try multiple services in sequence
  useEffect(() => {
    let cancelled = false;
    const tryGeo = async () => {
      // 1️⃣ ipapi.co
      try {
        const r = await fetch('https://ipapi.co/json/');
        const d = await r.json();
        if (!d.error && d.region_code && d.city) {
          if (!cancelled) setLocation({ city: d.city, stateCode: d.region_code, stateName: d.region, lat: d.latitude || null, lng: d.longitude || null });
          return;
        }
      } catch {}
      // 2️⃣ freeipapi.com fallback
      try {
        const r = await fetch('https://freeipapi.com/api/json');
        const d = await r.json();
        if (d.regionCode && d.cityName) {
          if (!cancelled) setLocation({ city: d.cityName, stateCode: d.regionCode, stateName: d.regionName || d.regionCode, lat: d.latitude || null, lng: d.longitude || null });
          return;
        }
      } catch {}
      // 3️⃣ ip-api.com fallback
      try {
        const r = await fetch('http://ip-api.com/json/?fields=status,city,regionName,region,lat,lon');
        const d = await r.json();
        if (d.status === 'success' && d.region && d.city) {
          if (!cancelled) setLocation({ city: d.city, stateCode: d.region, stateName: d.regionName || d.region, lat: d.lat || null, lng: d.lon || null });
          return;
        }
      } catch {}
      // all failed — location stays null, schools will still display
    };
    tryGeo().finally(() => { if (!cancelled) setLocating(false); });
    return () => { cancelled = true; };
  }, []);

  // Geocode each school and keep only those within 25 miles of the user
  useEffect(() => {
    if (locating || loadingSchools) return;
    if (!location?.lat || !location?.lng || allSchools.length === 0) {
      setNearbySchools([]);
      setGeocoding(false);
      return;
    }
    let cancelled = false;
    setGeocoding(true);
    const run = async () => {
      const results = [];
      for (const school of allSchools) {
        if (cancelled) break;
        // Check cache before deciding whether to rate-limit
        const city = school.city;
        const state = school.state;
        const zip = school.pincode;
        const cacheKey = city
          ? `sk2g_geo:${city?.toLowerCase()}:${(state || '').toLowerCase()}`
          : `sk2g_geo:zip:${zip}`;
        const wasCached = (() => { try { return !!sessionStorage.getItem(cacheKey); } catch { return false; } })();
        const coords = await geocodeSchool(city, state, zip);
        if (coords) {
          const dist = haversineMiles(location.lat, location.lng, coords.lat, coords.lng);
          if (dist <= 25) results.push({ ...school, _dist: parseFloat(dist.toFixed(1)) });
        }
        // Respect Nominatim's 1 req/sec policy only for uncached (network) lookups
        if (!wasCached) await new Promise(res => setTimeout(res, 1100));
      }
      if (!cancelled) {
        setNearbySchools(results.sort((a, b) => a._dist - b._dist));
        setGeocoding(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [allSchools, location, locating, loadingSchools]);

  // Search debounce
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      const q = search.toLowerCase();
      setSearchResults(allSchools.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.city?.toLowerCase().includes(q) ||
        s.state?.toLowerCase().includes(q)
      ));
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [search, allSchools]);

  const handleSelect = (school) => navigate(`/schools/${school.id}`);

  const isBaseReady = !locating && !loadingSchools;
  const isReady = isBaseReady && !geocoding;
  const showSearch = search.trim().length > 0;
  const stateLabel = location ? (STATE_NAMES[location.stateCode] || location.stateName || location.stateCode) : null;

  return (
    <div style={{ background: '#f8f7ff', minHeight: '100vh' }}>

      {/* ── Hero Banner ─────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4f46e5 75%, #7c3aed 100%)',
        padding: '3.5rem 1rem 5rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative blobs */}
        <div style={{ position:'absolute', top:-60, right:-60, width:260, height:260, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-80, left:-40, width:220, height:220, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />

        <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          
          <h1 style={{ color: '#fff', fontSize: 'clamp(1.9rem, 5vw, 3rem)', fontWeight: 900, lineHeight: 1.15, margin: '0 0 1rem' }}>
            Find the Perfect Kit<br />
            <span style={{ background: 'linear-gradient(90deg,#a5b4fc,#e879f9)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              for Your School
            </span>
          </h1>
          

          {/* Search Bar */}
          <div ref={searchRef} style={{ position: 'relative', maxWidth: 520, margin: '0 auto' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              background: '#fff', borderRadius: 50, padding: '0.7rem 1.2rem',
              boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
            }}>
              <span style={{ fontSize: '1.1rem' }}>🔍</span>
              <input
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.95rem', color: '#111', background: 'transparent' }}
                placeholder="Search school name, city or state..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {searching && <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>…</span>}
              {search && (
                <button onClick={() => setSearch('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:'1rem', lineHeight:1 }}>✕</button>
              )}
            </div>

            {/* Search dropdown */}
            {showSearch && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
                background: '#fff', borderRadius: 16, overflow: 'hidden',
                boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                zIndex: 200, maxHeight: 320, overflowY: 'auto',
              }}>
                {searchResults.length === 0 && !searching ? (
                  <div style={{ padding: '1.2rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.88rem' }}>
                    No schools found for "{search}"
                  </div>
                ) : searchResults.map(s => (
                  <div
                    key={s.id}
                    onClick={() => handleSelect(s)}
                    style={{ padding: '0.85rem 1.2rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition:'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f3ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>🏫 {s.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
                      {s.city}{s.state ? `, ${STATE_NAMES[s.state] || s.state}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Curved divider ─────────────────────────────────── */}
      <div style={{ marginTop: -2, lineHeight: 0 }}>
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display:'block', width:'100%' }}>
          <path d="M0 60 C360 0 1080 0 1440 60 L1440 0 L0 0 Z" fill="url(#heroGrad)" />
          <defs>
            <linearGradient id="heroGrad" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
              <stop stopColor="#1e1b4b"/>
              <stop offset="0.5" stopColor="#4f46e5"/>
              <stop offset="1" stopColor="#7c3aed"/>
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* ── Main Content ───────────────────────────────────── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem 4rem' }}>

        {/* Loading: detecting location */}
        {!isBaseReady && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem', animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>🌍</div>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Detecting your location…</p>
          </div>
        )}
        {/* Loading: geocoding school distances */}
        {isBaseReady && geocoding && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem', animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>📍</div>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Finding schools within 25 miles…</p>
          </div>
        )}

        {isReady && !showSearch && (
          <>
            {/* ── Schools within 25 miles ─── */}
            {nearbySchools.length > 0 && (
              <section style={{ marginBottom: '3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.4rem' }}>
                  <div style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: 12, padding: '0.5rem 0.75rem', fontSize: '1.2rem' }}>
                    📍
                  </div>
                  <div>
                    <h2 style={{ margin: 0, fontWeight: 800, color: '#111827', fontSize: '1.3rem' }}>
                      Schools within 25 miles
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                      {location ? `Near ${location.city}` : 'Near your location'} · {nearbySchools.length} school{nearbySchools.length !== 1 ? 's' : ''} found
                    </p>
                  </div>
                </div>
                <div style={gridStyle}>
                  {nearbySchools.map(s => (
                    <SchoolCard key={s.id} school={s} onSelect={handleSelect} highlight distance={s._dist} />
                  ))}
                </div>
              </section>
            )}

            {/* No schools within 25 miles */}
            {nearbySchools.length === 0 && allSchools.length > 0 && (
              <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📍</div>
                <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#374151', marginBottom: '0.5rem' }}>
                  No schools within 25 miles{location ? ` of ${location.city}` : ''}
                </div>
                <div style={{ fontSize: '0.88rem', color: '#6b7280' }}>
                  Try searching for a school by name above, or check back later as we add more schools.
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Search results ─── */}
        {isReady && showSearch && (
          <section>
            <div style={{ marginBottom: '1.2rem' }}>
              <h2 style={{ margin: 0, fontWeight: 800, color: '#111827', fontSize: '1.2rem' }}>
                Search results for "{search}"
              </h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#9ca3af' }}>
                {searchResults.length} school{searchResults.length !== 1 ? 's' : ''} found
              </p>
            </div>
            {searchResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#9ca3af' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🏫</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#374151', marginBottom: '0.4rem' }}>No schools found</div>
                <div style={{ fontSize: '0.85rem' }}>Try a different school name or city</div>
              </div>
            ) : (
              <div style={gridStyle}>
                {searchResults.map(s => (
                  <SchoolCard key={s.id} school={s} onSelect={handleSelect} highlight={false} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Empty state */}
        {isReady && allSchools.length === 0 && (
          <div style={{ textAlign: 'center', padding: '4rem 1rem', color: '#9ca3af' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>🏫</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#374151', marginBottom: '0.4rem' }}>No schools added yet</div>
            <div style={{ fontSize: '0.85rem' }}>Check back soon — schools are being added.</div>
          </div>
        )}

        {/* ── How it works ─────────────────────────────────── */}
        {isReady && !showSearch && (
          <section style={{ marginTop: '4rem', background: 'linear-gradient(135deg,#1e1b4b,#4f46e5)', borderRadius: 24, padding: '2.5rem 2rem', color: '#fff' }}>
            <h2 style={{ textAlign: 'center', fontWeight: 900, fontSize: '1.4rem', marginBottom: '2rem', color: '#fff' }}>
              🎒 How It Works
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '1.5rem', textAlign: 'center' }}>
              {[
                { icon: '🏫', step: '1', title: 'Pick Your School', desc: 'Select from schools near you or search by name.' },
                { icon: '🎓', step: '2', title: 'Choose Your Grade', desc: 'We show kits tailored to your grade level.' },
                { icon: '✏️', step: '3', title: 'Customise Your Kit', desc: 'Swap or remove items to tailor the kit — then add the whole thing to cart in one click.' },
                { icon: '🚚', step: '4', title: 'Fast Delivery', desc: 'We get it to you before school starts.' },
              ].map(({ icon, step, title, desc }) => (
                <div key={step}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{icon}</div>
                  <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, display: 'inline-block', padding: '1px 10px', fontSize: '0.68rem', fontWeight: 700, marginBottom: '0.5rem', letterSpacing: '0.05em' }}>
                    STEP {step}
                  </div>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '0.3rem' }}>{title}</div>
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
  gap: '1.1rem',
};

export default SchoolKitsLandingPage;
